import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, inputCls, EVENTS, StatCard, StatusPill, Skeleton, EmptyState } from './shared';

interface Advertiser {
  id: string;
  name: string;
  company_name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  is_active: boolean;
}

interface CampaignRow {
  id: string;
  name: string;
  campaign_type: string;
  utm_source: string | null;
  utm_campaign: string | null;
  aff_id: string | null;
  is_active: boolean;
  clicks: number;
  signups: number;
  spend: number;
  revenue: number;
  incentive: number;
  roi: number;
}

interface PostbackConfig {
  id: string;
  name: string;
  source: string | null;
  aff_id: string | null;
  event: string;
  url_template: string;
  method: string;
  is_active: boolean;
}

interface AdvertiserDetailData {
  advertiser: Advertiser;
  stats: { clicks: number; signups: number; revenue: number; spend: number; incentive: number; roi: number };
  campaigns: CampaignRow[];
  postback_configs: PostbackConfig[];
  global_postback_configs: PostbackConfig[];
}

const EMPTY_CAMPAIGN_FORM = {
  name: '', campaignType: 'paid', costModel: 'cpa', costValue: '',
  cpaEvent: 'signup', utmSource: '', utmMedium: '', utmCampaign: '', affId: '',
  postbackOverride: '', targetingGender: '', targetingAgeMin: '', targetingAgeMax: '',
  targetingStates: '', notes: '',
};

const EMPTY_POSTBACK_FORM = {
  name: '', event: 'signup', source: '', urlTemplate: '', method: 'GET',
};

export default function AdvertiserDetail({ advertiserId, onSelectCampaign }: {
  advertiserId: string;
  onSelectCampaign: (id: string) => void;
}) {
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ['admin', 'ua', 'advertiser', advertiserId],
    queryFn: () => apiFetch(`/api/ua/admin/advertisers/${advertiserId}`) as Promise<AdvertiserDetailData>,
  });

  const [campaignModal, setCampaignModal] = useState<{ mode: 'create' | 'edit'; id?: string } | null>(null);
  const [campaignForm, setCampaignForm] = useState(EMPTY_CAMPAIGN_FORM);
  const [postbackModal, setPostbackModal] = useState<{ mode: 'create' | 'edit'; id?: string } | null>(null);
  const [postbackForm, setPostbackForm] = useState(EMPTY_POSTBACK_FORM);
  const [saving, setSaving] = useState(false);

  if (detailQ.isLoading) return <Skeleton className="h-96" />;
  if (!detailQ.data) return <EmptyState title="Advertiser not found" />;

  const { advertiser, stats, campaigns, postback_configs, global_postback_configs } = detailQ.data;

  function openCreateCampaign() {
    setCampaignForm(EMPTY_CAMPAIGN_FORM);
    setCampaignModal({ mode: 'create' });
  }

  function openEditCampaign(c: CampaignRow) {
    setCampaignForm({
      name: c.name,
      campaignType: c.campaign_type,
      costModel: 'cpa', costValue: '', cpaEvent: 'signup',
      utmSource: c.utm_source ?? '', utmMedium: '', utmCampaign: c.utm_campaign ?? '',
      affId: c.aff_id ?? '', postbackOverride: '',
      targetingGender: '', targetingAgeMin: '', targetingAgeMax: '',
      targetingStates: '', notes: '',
    });
    setCampaignModal({ mode: 'edit', id: c.id });
  }

  async function saveCampaign() {
    setSaving(true);
    try {
      const body = {
        ...campaignForm,
        sourceName: advertiser.name,
        sourceId: advertiser.id,
        costValue: parseFloat(campaignForm.costValue) || 0,
        targetingAgeMin: campaignForm.targetingAgeMin ? parseInt(campaignForm.targetingAgeMin, 10) : undefined,
        targetingAgeMax: campaignForm.targetingAgeMax ? parseInt(campaignForm.targetingAgeMax, 10) : undefined,
      };
      if (campaignModal?.mode === 'edit' && campaignModal.id) {
        await apiFetch(`/api/ua/admin/campaigns/${campaignModal.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      } else {
        await apiFetch('/api/ua/admin/campaigns', { method: 'POST', body: JSON.stringify(body) });
      }
      setCampaignModal(null);
      setCampaignForm(EMPTY_CAMPAIGN_FORM);
      void qc.invalidateQueries({ queryKey: ['admin', 'ua', 'advertiser', advertiserId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'ua', 'campaigns'] });
    } catch { /* ignore */ }
    setSaving(false);
  }

  async function toggleCampaign(id: string, active: boolean) {
    try {
      await apiFetch(`/api/ua/admin/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify({ isActive: !active }) });
      void qc.invalidateQueries({ queryKey: ['admin', 'ua', 'advertiser', advertiserId] });
    } catch { /* ignore */ }
  }

  function openCreatePostback() {
    setPostbackForm(EMPTY_POSTBACK_FORM);
    setPostbackModal({ mode: 'create' });
  }

  function openEditPostback(p: PostbackConfig) {
    setPostbackForm({
      name: p.name,
      event: p.event,
      source: p.source ?? '',
      urlTemplate: p.url_template,
      method: p.method,
    });
    setPostbackModal({ mode: 'edit', id: p.id });
  }

  async function savePostback() {
    setSaving(true);
    try {
      // Blank source = fall back to the advertiser name so the config is
      // still scoped to this advertiser (vs becoming a true global config).
      // Non-blank = user-entered utm_source override.
      const sourceValue = postbackForm.source.trim() || advertiser.name;
      const body = {
        name: postbackForm.name,
        event: postbackForm.event,
        source: sourceValue,
        urlTemplate: postbackForm.urlTemplate,
        method: postbackForm.method,
      };
      if (postbackModal?.mode === 'edit' && postbackModal.id) {
        await apiFetch(`/api/ua/admin/postback-configs/${postbackModal.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      } else {
        await apiFetch('/api/ua/admin/postback-configs', { method: 'POST', body: JSON.stringify(body) });
      }
      setPostbackModal(null);
      setPostbackForm(EMPTY_POSTBACK_FORM);
      void qc.invalidateQueries({ queryKey: ['admin', 'ua', 'advertiser', advertiserId] });
    } catch { /* ignore */ }
    setSaving(false);
  }

  async function togglePostback(id: string) {
    try {
      await apiFetch(`/api/ua/admin/postback-configs/${id}/toggle`, { method: 'POST' });
      void qc.invalidateQueries({ queryKey: ['admin', 'ua', 'advertiser', advertiserId] });
    } catch { /* ignore */ }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800 rounded-xl p-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-white">{advertiser.name}</h2>
            <StatusPill active={advertiser.is_active} />
          </div>
          <p className="text-xs text-slate-400 mt-1">
            {advertiser.company_name ?? '—'}
            {advertiser.contact_email && <> · {advertiser.contact_email}</>}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Clicks" value={stats.clicks} />
        <StatCard label="Signups" value={stats.signups} tone="info" />
        <StatCard label="Revenue" value={`$${stats.revenue.toFixed(2)}`} tone="good" />
        <StatCard label="Spend" value={`$${stats.spend.toFixed(2)}`} tone="bad" sub={`Incentive $${stats.incentive.toFixed(2)}`} />
        <StatCard label="ROI" value={`${stats.roi.toFixed(1)}%`} tone={stats.roi >= 0 ? 'good' : 'bad'} />
      </div>

      {/* Campaigns table */}
      <div className="bg-slate-800 rounded-xl overflow-x-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="text-sm font-bold text-white">Campaigns</h3>
          <button onClick={openCreateCampaign} className="bg-primary text-white px-4 py-1.5 rounded-lg text-xs font-medium">
            + Create Campaign
          </button>
        </div>
        {campaigns.length === 0 ? (
          <div className="text-center text-slate-500 py-10 text-sm">No campaigns yet for this advertiser.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2 text-right">Clicks</th>
                <th className="px-3 py-2 text-right">Signups</th>
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right">Spend</th>
                <th className="px-3 py-2 text-right">ROI</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {campaigns.map((c) => (
                <tr key={c.id} className="hover:bg-slate-700/50 cursor-pointer" onClick={() => onSelectCampaign(c.id)}>
                  <td className="px-3 py-2 text-white">{c.name}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs uppercase">{c.campaign_type}</td>
                  <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{c.clicks}</td>
                  <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{c.signups}</td>
                  <td className="px-3 py-2 text-right text-green-400 tabular-nums">${c.revenue.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-red-400 tabular-nums">${c.spend.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-white tabular-nums">{c.roi.toFixed(1)}%</td>
                  <td className="px-3 py-2"><StatusPill active={c.is_active} /></td>
                  <td className="px-3 py-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openEditCampaign(c)} className="text-xs text-primary hover:underline">Edit</button>
                    <button onClick={() => void toggleCampaign(c.id, c.is_active)} className="text-xs text-slate-400 hover:text-white">
                      {c.is_active ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Postbacks section */}
      <div className="bg-slate-800 rounded-xl overflow-x-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <div>
            <h3 className="text-sm font-bold text-white">Postback Configs</h3>
            <p className="text-xs text-slate-500 mt-0.5">Matched by `source=&quot;{advertiser.name}&quot;` at postback fire time.</p>
          </div>
          <button onClick={openCreatePostback} className="bg-primary text-white px-4 py-1.5 rounded-lg text-xs font-medium">
            + Add Postback
          </button>
        </div>
        {postback_configs.length === 0 ? (
          <div className="text-center text-slate-500 py-10 text-sm">No advertiser-specific postbacks configured.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">URL</th>
                <th className="px-3 py-2">Method</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {postback_configs.map((p) => (
                <tr key={p.id} className="hover:bg-slate-700/50">
                  <td className="px-3 py-2 text-white">{p.name}</td>
                  <td className="px-3 py-2 text-primary text-xs">{p.event}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs font-mono truncate max-w-[280px]">{p.url_template}</td>
                  <td className="px-3 py-2 text-slate-300 text-xs">{p.method}</td>
                  <td className="px-3 py-2"><StatusPill active={p.is_active} /></td>
                  <td className="px-3 py-2 flex gap-2">
                    <button onClick={() => openEditPostback(p)} className="text-xs text-primary hover:underline">Edit</button>
                    <button onClick={() => void togglePostback(p.id)} className="text-xs text-slate-400 hover:text-white">
                      {p.is_active ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {global_postback_configs.length > 0 && (
        <details className="bg-slate-800 rounded-xl">
          <summary className="px-5 py-3 text-sm font-medium text-slate-400 cursor-pointer hover:text-white">
            Global postbacks ({global_postback_configs.length}) — fire for every advertiser
          </summary>
          <div className="overflow-x-auto border-t border-slate-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Event</th>
                  <th className="px-3 py-2">URL</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {global_postback_configs.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2 text-white">{p.name}</td>
                    <td className="px-3 py-2 text-primary text-xs">{p.event}</td>
                    <td className="px-3 py-2 text-slate-400 text-xs font-mono truncate max-w-[280px]">{p.url_template}</td>
                    <td className="px-3 py-2"><StatusPill active={p.is_active} /></td>
                    <td className="px-3 py-2">
                      <button onClick={() => void togglePostback(p.id)} className="text-xs text-slate-400 hover:text-white">
                        {p.is_active ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* Campaign modal */}
      {campaignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8" onClick={() => setCampaignModal(null)}>
          <div className="bg-slate-800 rounded-xl p-5 w-full max-w-lg space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-white">
              {campaignModal.mode === 'edit' ? 'Edit Campaign' : 'Create Campaign'} · {advertiser.name}
            </h3>
            <input value={campaignForm.name} onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })} placeholder="Campaign name *" className={inputCls + ' w-full'} />
            <div className="grid grid-cols-3 gap-2">
              <select value={campaignForm.campaignType} onChange={(e) => setCampaignForm({ ...campaignForm, campaignType: e.target.value })} className={inputCls}>
                <option value="paid">Paid</option>
                <option value="organic">Organic</option>
                <option value="referral">Referral</option>
                <option value="influencer">Influencer</option>
              </select>
              <select value={campaignForm.costModel} onChange={(e) => setCampaignForm({ ...campaignForm, costModel: e.target.value })} className={inputCls}>
                <option value="cpa">CPA</option>
                <option value="cpc">CPC</option>
                <option value="flat">Flat</option>
                <option value="none">None</option>
              </select>
              <input type="number" step="0.01" value={campaignForm.costValue} onChange={(e) => setCampaignForm({ ...campaignForm, costValue: e.target.value })} placeholder="$ value" className={inputCls} />
            </div>
            {campaignForm.costModel === 'cpa' && (
              <select value={campaignForm.cpaEvent} onChange={(e) => setCampaignForm({ ...campaignForm, cpaEvent: e.target.value })} className={inputCls + ' w-full'}>
                {EVENTS.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            )}
            <div className="grid grid-cols-3 gap-2">
              <input value={campaignForm.utmSource} onChange={(e) => setCampaignForm({ ...campaignForm, utmSource: e.target.value })} placeholder="utm_source" className={inputCls} />
              <input value={campaignForm.utmCampaign} onChange={(e) => setCampaignForm({ ...campaignForm, utmCampaign: e.target.value })} placeholder="utm_campaign" className={inputCls} />
              <input value={campaignForm.affId} onChange={(e) => setCampaignForm({ ...campaignForm, affId: e.target.value })} placeholder="aff_id" className={inputCls} />
            </div>
            <input value={campaignForm.postbackOverride} onChange={(e) => setCampaignForm({ ...campaignForm, postbackOverride: e.target.value })} placeholder="Postback URL override (optional)" className={inputCls + ' w-full font-mono text-xs'} />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setCampaignModal(null)} className="text-slate-400 text-sm px-3 py-1">Cancel</button>
              <button onClick={() => void saveCampaign()} disabled={!campaignForm.name || saving} className="bg-primary text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Postback modal */}
      {postbackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPostbackModal(null)}>
          <div className="bg-slate-800 rounded-xl p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-white">
              {postbackModal.mode === 'edit' ? 'Edit Postback' : 'Add Postback'} · {advertiser.name}
            </h3>
            <input value={postbackForm.name} onChange={(e) => setPostbackForm({ ...postbackForm, name: e.target.value })} placeholder="Name *" className={inputCls + ' w-full'} />
            <select value={postbackForm.event} onChange={(e) => setPostbackForm({ ...postbackForm, event: e.target.value })} className={inputCls + ' w-full'}>
              {EVENTS.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
            <div>
              <input
                value={postbackForm.source}
                onChange={(e) => setPostbackForm({ ...postbackForm, source: e.target.value })}
                placeholder="Source match (optional)"
                className={inputCls + ' w-full'}
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Enter the utm_source value to match (e.g. &apos;testaffiliate&apos;), or leave blank to match all sources for this advertiser.
              </p>
            </div>
            <textarea value={postbackForm.urlTemplate} onChange={(e) => setPostbackForm({ ...postbackForm, urlTemplate: e.target.value })} placeholder="Postback URL *" rows={2} className={inputCls + ' w-full font-mono text-xs resize-none'} />
            <p className="text-[10px] text-slate-500">
              Macros: {'{member_id}'} {'{click_id}'} {'{aff_id}'} {'{sub1}'} {'{sub2}'} {'{sub3}'} {'{payout}'} {'{event}'} {'{timestamp}'}
            </p>
            <select value={postbackForm.method} onChange={(e) => setPostbackForm({ ...postbackForm, method: e.target.value })} className={inputCls + ' w-full'}>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
            </select>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setPostbackModal(null)} className="text-slate-400 text-sm px-3 py-1">Cancel</button>
              <button onClick={() => void savePostback()} disabled={!postbackForm.name || !postbackForm.urlTemplate || saving} className="bg-primary text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
